import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Typography, Progress, Alert, Spin, Divider,
  Row, Col, Table, Space, Tooltip, Skeleton, Empty, Modal, Form, Input, message,
} from 'antd';
import {
  CrownOutlined, RocketOutlined, CheckCircleOutlined, WarningOutlined,
  ThunderboltOutlined, ExclamationCircleOutlined, CreditCardOutlined,
  FileTextOutlined, DownloadOutlined, LinkOutlined, EyeOutlined,
  CalendarOutlined, ReloadOutlined, LockOutlined, BankOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text, Paragraph } = Typography;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* ── Helpers ── */
// Detect if the user is in India via their timezone (IST = Asia/Kolkata or Asia/Calcutta)
const detectIndiaUser = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta';
  } catch { return false; }
};

const IS_INDIA   = detectIndiaUser();
const CURRENCY   = IS_INDIA ? 'INR' : 'USD';
const PRICE_DISP = IS_INDIA ? '₹999/mo' : '$9.99/mo';
const PRICE_FULL = IS_INDIA ? '₹999 / month via Razorpay' : '$9.99 / month via Razorpay';

// Region-aware placeholder examples (avoid signalling a specific HQ country)
const PH = IS_INDIA
  ? { addr1: '123 MG Road', city: 'Mumbai', state: 'Maharashtra', postal: '400001', country: 'India',         tax: 'GSTIN (optional)' }
  : { addr1: '123 Market St', city: 'San Francisco', state: 'California', postal: '94105', country: 'United States', tax: 'EIN / VAT (optional)' };

const fmtCurrency = (amount, currency = 'inr') => {
  const locale = currency.toLowerCase() === 'inr' ? 'en-IN' : 'en-US';
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
};

const fmtDate = (d) => {
  if (!d) return '—';
  const date = typeof d === 'number' ? new Date(d * 1000) : new Date(d);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};
const fmtDateShort = (d) => {
  if (!d) return '—';
  const date = typeof d === 'number' ? new Date(d * 1000) : new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CARD_BRAND_COLORS = {
  visa:       '#1a1f71',
  mastercard: '#eb001b',
  amex:       '#007bc1',
  discover:   '#ff6600',
  default:    '#0a40d9',
};

const CARD_BRAND_LABEL = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express',
  discover: 'Discover', jcb: 'JCB', diners: 'Diners Club', unionpay: 'UnionPay',
};

const PRO_FEATURES = [
  'Unlimited campaigns per month',
  'Email automations & welcome series',
  'Signup forms — embeddable & hosted',
  'Dynamic / behavioral segments',
  'Timezone send scheduling',
  'Ready-to-use starter templates',
  'Resend to non-openers',
  'Advanced analytics & reports',
  'Unlimited sender domains',
  'Team members — Admin, Manager, Viewer',
  'Custom reply-to & preview text',
  'Priority email support',
];

// Free vs Pro — values: true = included, false = not available, string = label.
const PLAN_COMPARISON = [
  { label: 'Campaigns per month',            free: '1',     pro: 'Unlimited' },
  { label: 'Subscribers & lists',            free: true,    pro: true },
  { label: 'Drag-and-drop builder + templates', free: true, pro: true },
  { label: 'Bring your own AWS SES',         free: true,    pro: true },
  { label: 'Open & click analytics',         free: 'Basic', pro: 'Advanced' },
  { label: 'Email automations',              free: false,   pro: true },
  { label: 'Signup forms',                   free: false,   pro: true },
  { label: 'Dynamic / behavioral segments',  free: false,   pro: true },
  { label: 'Team members & roles',           free: false,   pro: true },
  { label: 'Priority support',               free: false,   pro: true },
];

const cmpCell = (v, pro) => {
  if (v === true)  return <CheckCircleOutlined style={{ color: pro ? 'var(--brand-primary)' : 'var(--ok)', fontSize: 15 }} />;
  if (v === false) return <span style={{ color: 'var(--text-3)', fontSize: 15 }}>—</span>;
  return <span style={{ fontSize: 13, fontWeight: pro ? 600 : 400, color: pro ? 'var(--brand-primary)' : 'var(--text-2)' }}>{v}</span>;
};

/* ── Sub-components ── */
const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
    textTransform: 'uppercase', marginBottom: 12,
    color: 'var(--text-3)',
  }}>
    {children}
  </div>
);

const InfoRow = ({ label, value, valueStyle = {} }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 0', borderBottom: '1px solid var(--border)',
  }}>
    <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>{label}</Text>
    <Text style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', ...valueStyle }}>{value}</Text>
  </div>
);

/* ── Invoice status pill ── */
const InvoicePill = ({ status }) => {
  const toneMap = { paid: 'ok', open: 'warn', void: null, uncollectible: 'danger' };
  const tone = toneMap[status];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 500, textTransform: 'capitalize',
      background: tone ? `var(--${tone}-bg)` : 'var(--surface-2)',
      color:      tone ? `var(--${tone})`    : 'var(--text-2)',
      border:     tone ? `1px solid var(--${tone})` : '1px solid var(--border)',
    }}>
      {status}
    </span>
  );
};

/* ── Main ── */
const Billing = () => {
  const [status,      setStatus]      = useState(null);
  const [invoices,    setInvoices]    = useState(null);
  const [upcoming,    setUpcoming]    = useState(null);
  const [mainLoading, setMainLoading] = useState(true);
  const [upgrading,   setUpgrading]   = useState(false);
  const [canceling,   setCanceling]   = useState(false);
  const [portaling,   setPortaling]   = useState(false);
  const [error,       setError]       = useState('');
  const [successMsg,  setSuccessMsg]  = useState('');
  const [billingModal, setBillingModal] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [billingForm] = Form.useForm();

  const params   = new URLSearchParams(window.location.search);
  const canceled = params.get('canceled') === '1';

  const loadAll = useCallback(async () => {
    setMainLoading(true);
    try {
      const res = await axios.get('/api/billing/status', { headers: authHeader() });
      setStatus(res.data);

      Promise.allSettled([
        axios.get('/api/billing/invoices',         { headers: authHeader() }),
        axios.get('/api/billing/upcoming-invoice', { headers: authHeader() }),
      ]).then(([invRes, upRes]) => {
        if (invRes.status === 'fulfilled') setInvoices(invRes.value.data);
        else                               setInvoices({ invoices: [] });
        if (upRes.status === 'fulfilled')  setUpcoming(upRes.value.data);
        else                               setUpcoming({ upcoming: null });
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load billing information');
    } finally {
      setMainLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    if (canceled) window.history.replaceState({}, '', '/billing');
  }, []);

  // ── Load Razorpay checkout script dynamically ────────────────────────────
  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src   = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  // Entry point: check billing profile first. If business name + country are missing,
  // collect them in a modal before opening Razorpay. Otherwise go straight to checkout.
  const handleUpgrade = async () => {
    setError('');
    try {
      const { data } = await axios.get('/api/settings/billing-profile', { headers: authHeader() });
      const complete = data?.billing_name?.trim() && data?.billing_country?.trim();
      if (complete) {
        proceedToRazorpay();
      } else {
        // Pre-fill whatever exists (fall back to org name for business name)
        billingForm.setFieldsValue({
          billing_name:        data?.billing_name || data?.name || '',
          billing_email:       data?.billing_email || '',
          billing_address1:    data?.billing_address1 || '',
          billing_address2:    data?.billing_address2 || '',
          billing_city:        data?.billing_city || '',
          billing_state:       data?.billing_state || '',
          billing_country:     data?.billing_country || (IS_INDIA ? 'India' : ''),
          billing_postal_code: data?.billing_postal_code || '',
          billing_tax_id:      data?.billing_tax_id || '',
        });
        setBillingModal(true);
      }
    } catch {
      // If profile fetch fails, don't block the upgrade — show the modal empty
      billingForm.setFieldsValue({ billing_country: IS_INDIA ? 'India' : '' });
      setBillingModal(true);
    }
  };

  // Save the billing profile, then continue to Razorpay
  const handleBillingSubmit = async (values) => {
    setSavingProfile(true);
    try {
      await axios.put('/api/settings/billing-profile', values, { headers: authHeader() });
      setBillingModal(false);
      proceedToRazorpay();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to save billing details');
    } finally {
      setSavingProfile(false);
    }
  };

  const proceedToRazorpay = async () => {
    setUpgrading(true); setError('');
    try {
      // Step 1: get subscription_id + public key from backend
      const res = await axios.post('/api/billing/checkout', { currency: CURRENCY }, { headers: authHeader() });
      const { subscription_id, key_id, prefill } = res.data;

      // Step 2: load Razorpay script
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load Razorpay. Please check your internet connection.');

      // Step 3: open Razorpay checkout popup
      await new Promise((resolve, reject) => {
        const options = {
          key:             key_id,
          subscription_id: subscription_id,
          name:            'SES Mailbox',
          description:     'Pro Plan — Monthly Subscription',
          image:           `${window.location.origin}/logo192.png`,
          prefill,
          theme:           { color: '#0a40d9' },
          modal: {
            ondismiss: () => reject(new Error('Payment cancelled')),
          },
          handler: async (response) => {
            try {
              // Step 4: verify payment on backend
              const cap = await axios.post('/api/billing/capture', {
                razorpay_payment_id:      response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature:       response.razorpay_signature,
              }, { headers: authHeader() });

              // Store the fresh JWT so the sidebar plan + Pro-gated nav update immediately
              if (cap.data?.token) localStorage.setItem('token', cap.data.token);

              setSuccessMsg("🎉 You're now on Pro! Your subscription is active.");
              await loadAll();
              resolve();
              // Reload so the whole shell (sidebar, nav locks) reflects Pro
              setTimeout(() => window.location.reload(), 1200);
            } catch (captureErr) {
              reject(captureErr);
            }
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (response) => {
          reject(new Error(response.error?.description || 'Payment failed'));
        });
        rzp.open();
      });
    } catch (err) {
      if (err.message !== 'Payment cancelled') {
        setError(err.response?.data?.message || err.message || 'Upgrade failed');
      }
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure? Your Pro access will end at the current billing period.')) return;
    setCanceling(true); setError('');
    try {
      const res = await axios.post('/api/billing/cancel', {}, { headers: authHeader() });
      setSuccessMsg(res.data.message || 'Subscription cancelled.');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const handlePortal = async () => {
    setPortaling(true); setError('');
    try {
      const res = await axios.post('/api/billing/portal', {}, { headers: authHeader() });
      window.open(res.data.url, '_blank');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open payment management');
      setPortaling(false);
    }
  };

  if (mainLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Spin size="large" />
    </div>
  );

  const isPro     = status?.plan === 'pro';
  const isPastDue = status?.plan_status === 'past_due';
  const usedCount = status?.campaigns_this_month ?? 0;
  const allowed   = status?.campaigns_allowed;
  const usePct    = allowed ? Math.min(100, (usedCount / allowed) * 100) : 0;

  const cardStyle = {
    borderRadius: 'var(--r-lg)',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow-sm)',
    background: 'var(--surface)',
  };

  /* ── Invoice PDF helpers ── */
  const downloadPDF = async (inv) => {
    try {
      const res = await axios.get(`/api/billing/invoices/${inv.id}/pdf`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  const viewPDF = async (inv) => {
    try {
      const res = await axios.get(`/api/billing/invoices/${inv.id}/pdf`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { /* silent */ }
  };

  /* ── Invoice table columns ── */
  const invoiceCols = [
    {
      title: 'Invoice #', key: 'number',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
            {r.invoice_number}
          </Text>
          <Text style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.description || 'Pro Plan'}</Text>
        </Space>
      ),
    },
    {
      title: 'Period', key: 'period', width: 200,
      render: (_, r) => (
        <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {fmtDateShort(r.period_start)} – {fmtDateShort(r.period_end)}
        </Text>
      ),
    },
    {
      title: 'Date', dataIndex: 'created_at', key: 'created_at', width: 130,
      render: v => <Text style={{ fontSize: 13, color: 'var(--text-2)' }}>{fmtDateShort(v)}</Text>,
    },
    {
      title: 'Amount', key: 'amount', width: 110,
      render: (_, r) => (
        <Text strong style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
          {fmtCurrency(r.amount, r.currency)}
        </Text>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 90,
      render: s => <InvoicePill status={s} />,
    },
    {
      title: '', key: 'actions', width: 80,
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title="View PDF">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => viewPDF(r)} />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button type="text" size="small" icon={<DownloadOutlined />} onClick={() => downloadPDF(r)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', width: '100%' }}>

      {/* ── Top bar actions ── */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <Button icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button>
      </div>

      {/* ── Banners ── */}
      {successMsg && (
        <Alert type="success" showIcon icon={<CheckCircleOutlined />}
          message="Success" description={successMsg}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} closable onClose={() => setSuccessMsg('')}
        />
      )}
      {canceled && (
        <Alert type="info" showIcon message="Checkout canceled"
          description="Your plan was not changed. You can upgrade any time."
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} closable
        />
      )}
      {isPastDue && (
        <Alert type="warning" showIcon icon={<WarningOutlined />}
          message="Payment overdue"
          description="Please update your payment method to keep Pro access."
          action={<Button size="small" onClick={handlePortal} loading={portaling}>Update Payment Method</Button>}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
        />
      )}
      {error && (
        <Alert type="error" message={error} showIcon closable onClose={() => setError('')}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
        />
      )}

      {/* ── Row 1: Plan + Usage ── */}
      <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>

        {/* Current plan */}
        <Col xs={24} md={12}>
          <Card style={{ ...cardStyle, height: '100%' }}>
            <SectionLabel>Current Plan</SectionLabel>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: isPro
                    ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                    : 'var(--surface-2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {isPro
                    ? <CrownOutlined style={{ fontSize: 22, color: '#fff' }} />
                    : <RocketOutlined style={{ fontSize: 22, color: 'var(--text-3)' }} />
                  }
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 750, fontSize: 22, color: 'var(--text)', letterSpacing: '-0.03em' }}>
                      {isPro ? 'Pro' : 'Free'}
                    </span>
                    {isPro ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                        background: 'var(--warn-bg)', color: 'var(--warn)',
                        border: '1px solid var(--warn)', fontSize: 12, fontWeight: 600,
                      }}>
                        Active
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                        background: 'var(--surface-2)', color: 'var(--text-3)',
                        border: '1px solid var(--border)', fontSize: 12,
                      }}>
                        Free Tier
                      </span>
                    )}
                  </div>
                  <Text style={{ color: 'var(--text-3)', fontSize: 12 }}>
                    {isPro ? PRICE_FULL : 'No charge'}
                  </Text>
                </div>
              </div>
            </div>

            <Divider style={{ margin: '0 0 16px', borderColor: 'var(--border)' }} />

            {isPro ? (
              <>
                {upcoming?.upcoming && (
                  <InfoRow
                    label="Next charge"
                    value={`${fmtCurrency(upcoming.upcoming.amount_due, upcoming.upcoming.currency)} on ${fmtDate(upcoming.upcoming.next_date)}`}
                    valueStyle={{ color: 'var(--brand-primary)' }}
                  />
                )}
                {status?.plan_expires_at && (
                  <InfoRow
                    label="Renews"
                    value={new Date(status.plan_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  />
                )}
                <InfoRow label="Billing cycle" value="Monthly" />
                <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button onClick={handlePortal} loading={portaling} style={{ borderRadius: 'var(--r-md)' }}>
                    Manage Billing
                  </Button>
                  <Button danger onClick={handleCancel} loading={canceling} style={{ borderRadius: 'var(--r-md)' }}>
                    Cancel Plan
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Paragraph style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 20 }}>
                  Free plan includes 1 campaign per month. Upgrade to Pro for unlimited campaigns, email automations, signup forms, dynamic segments, timezone scheduling, and priority support.
                </Paragraph>
                <Button
                  type="primary" icon={<ThunderboltOutlined />}
                  onClick={handleUpgrade} loading={upgrading}
                  size="large" block
                  style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}
                >
                  Upgrade to Pro — {PRICE_DISP}
                </Button>
              </>
            )}
          </Card>
        </Col>

        {/* Usage */}
        <Col xs={24} md={12}>
          <Card style={{ ...cardStyle, height: '100%' }}>
            <SectionLabel>This Month's Usage</SectionLabel>

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <Text style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>Campaigns Sent</Text>
                <Text style={{ color: 'var(--text-3)', fontSize: 13, fontFamily: 'var(--font-mono)' }}>
                  {isPro
                    ? <><strong style={{ color: 'var(--text)' }}>{usedCount}</strong> sent (unlimited)</>
                    : <><strong style={{ color: usedCount >= allowed ? 'var(--danger)' : 'var(--text)' }}>{usedCount}</strong> / {allowed}</>
                  }
                </Text>
              </div>
              {!isPro ? (
                <Progress
                  percent={usePct}
                  status={usePct >= 100 ? 'exception' : undefined}
                  strokeColor={usePct >= 100 ? 'var(--danger)' : 'var(--brand-primary)'}
                  trailColor="var(--surface-2)"
                  showInfo={false}
                  strokeWidth={8}
                />
              ) : (
                <Progress
                  percent={100}
                  strokeColor={{ from: '#f59e0b', to: '#ef4444' }}
                  trailColor="var(--surface-2)"
                  showInfo={false}
                  strokeWidth={8}
                />
              )}
              {!isPro && usedCount >= allowed && (
                <Text style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4, display: 'block' }}>
                  Limit reached — upgrade for unlimited sends
                </Text>
              )}
            </div>

            <Divider style={{ margin: '0 0 12px', borderColor: 'var(--border)' }} />

            {[
              { label: 'Subscribers',    value: 'Unlimited',           ok: true },
              { label: 'Mailing Lists',  value: 'Unlimited',           ok: true },
              { label: 'Campaigns / mo', value: isPro ? 'Unlimited' : `${allowed}`, ok: isPro },
              { label: 'Own AWS SES',    value: 'Yes — bring your own', ok: true },
              { label: 'Analytics',      value: isPro ? 'Advanced' : 'Basic', ok: isPro },
            ].map(({ label, value, ok }) => (
              <div key={label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0',
              }}>
                <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>{label}</Text>
                <Space size={6}>
                  {ok
                    ? <CheckCircleOutlined style={{ color: 'var(--ok)', fontSize: 13 }} />
                    : <ExclamationCircleOutlined style={{ color: 'var(--warn)', fontSize: 13 }} />
                  }
                  <Text style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{value}</Text>
                </Space>
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      {/* ── Row 2: Payment Method + Upcoming Invoice ── */}
      <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>

        {/* Payment method */}
        <Col xs={24} md={12}>
          <Card style={cardStyle}>
            <SectionLabel>Payment Method</SectionLabel>
            {!isPro ? (
              <div style={{ padding: '16px 0', textAlign: 'center' }}>
                <LockOutlined style={{ fontSize: 28, color: 'var(--text-3)', display: 'block', marginBottom: 8 }} />
                <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>No billing info — you're on the free plan.</Text>
                <div style={{ marginTop: 12 }}>
                  <Button type="primary" size="small" onClick={handleUpgrade} loading={upgrading} style={{ borderRadius: 'var(--r-md)' }}>
                    Upgrade to Pro
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div style={{
                  borderRadius: 'var(--r-lg)',
                  background: 'linear-gradient(135deg, #2d6be4, #072654)',
                  padding: '20px 22px', marginBottom: 16,
                  position: 'relative', overflow: 'hidden',
                }}>
                  <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                  <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 20, fontWeight: 700, marginBottom: 8, letterSpacing: 1 }}>Razorpay</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>Subscription payments processed securely via Razorpay.</div>
                  <div style={{ marginTop: 12 }}>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                      Sub ID: {status?.razorpay_sub_id ? `${status.razorpay_sub_id.slice(0, 12)}…` : '—'}
                    </Text>
                  </div>
                </div>
                <Button onClick={handlePortal} loading={portaling} style={{ borderRadius: 'var(--r-md)' }}>
                  Manage Billing →
                </Button>
              </>
            )}
          </Card>
        </Col>

        {/* Upcoming invoice */}
        <Col xs={24} md={12}>
          <Card style={cardStyle}>
            <SectionLabel>Next Invoice</SectionLabel>

            {upcoming === null ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : !isPro ? (
              <div style={{ padding: '16px 0', textAlign: 'center' }}>
                <CalendarOutlined style={{ fontSize: 28, color: 'var(--text-3)', display: 'block', marginBottom: 8 }} />
                <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>No upcoming charges — you're on the free plan.</Text>
              </div>
            ) : !upcoming?.upcoming ? (
              <div style={{ padding: '16px 0', textAlign: 'center' }}>
                <CalendarOutlined style={{ fontSize: 28, color: 'var(--text-3)', display: 'block', marginBottom: 8 }} />
                <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>No upcoming invoice found.</Text>
              </div>
            ) : (
              <>
                <div style={{
                  borderRadius: 'var(--r-lg)', padding: '20px 22px', marginBottom: 16,
                  background: 'var(--brand-tint)',
                  border: '1px solid #0a40d933',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <Text style={{ color: 'var(--text-3)', fontSize: 12, display: 'block', marginBottom: 4 }}>Due on</Text>
                      <Text strong style={{ fontSize: 15, color: 'var(--text)' }}>{fmtDate(upcoming.upcoming.next_date)}</Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text style={{ color: 'var(--text-3)', fontSize: 12, display: 'block', marginBottom: 4 }}>Amount</Text>
                      <Text strong style={{ fontSize: 22, color: 'var(--brand-primary)', fontFamily: 'var(--font-mono)' }}>
                        {fmtCurrency(upcoming.upcoming.amount_due, upcoming.upcoming.currency)}
                      </Text>
                    </div>
                  </div>
                </div>

                <InfoRow label="Plan"           value={upcoming.upcoming.description || 'Pro Plan'} />
                <InfoRow label="Billing period" value={`Until ${fmtDate(upcoming.upcoming.period_end)}`} />
                <div style={{ marginTop: 12 }}>
                  <Button onClick={handlePortal} loading={portaling} style={{ borderRadius: 'var(--r-md)' }}>
                    Manage Billing →
                  </Button>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* ── Invoice History ── */}
      <Card style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <SectionLabel>Invoice History</SectionLabel>
            <span style={{ fontWeight: 650, fontSize: 15, color: 'var(--text)' }}>Past Invoices</span>
          </div>
          {isPro && (
            <Button onClick={handlePortal} loading={portaling} icon={<LinkOutlined />} style={{ borderRadius: 'var(--r-md)' }}>
              Manage Billing
            </Button>
          )}
        </div>

        {invoices === null ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : !invoices.invoices?.length ? (
          <Empty
            image={<FileTextOutlined style={{ fontSize: 40, color: 'var(--text-3)' }} />}
            imageStyle={{ height: 50 }}
            description={
              <Space direction="vertical" size={4} style={{ textAlign: 'center' }}>
                <Text style={{ color: 'var(--text-3)' }}>No invoices yet</Text>
                <Text style={{ color: 'var(--text-3)', fontSize: 12 }}>
                  {isPro
                    ? 'Invoices are generated on each billing cycle.'
                    : 'Invoices will appear here after you upgrade to Pro.'}
                </Text>
              </Space>
            }
          >
            {!isPro && (
              <Button type="primary" onClick={handleUpgrade} loading={upgrading} style={{ borderRadius: 'var(--r-md)' }}>
                Upgrade to Pro
              </Button>
            )}
          </Empty>
        ) : (
          <Table
            rowKey="id"
            dataSource={invoices.invoices}
            columns={invoiceCols}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={invoices.invoices.length > 10 ? { pageSize: 10, showTotal: n => `${n} invoices` } : false}
            style={{ borderRadius: 8, overflow: 'hidden' }}
          />
        )}
      </Card>

      {/* ── Free vs Pro comparison (free plan only) ── */}
      {!isPro && (
        <Card style={{ ...cardStyle, marginTop: 20 }}>
          <SectionLabel>Free vs Pro</SectionLabel>
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 84px 84px', alignItems: 'center', padding: '0 0 8px', borderBottom: '1px solid var(--border)' }}>
              <Text style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.06em' }}>FEATURE</Text>
              <Text style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textAlign: 'center', letterSpacing: '0.06em' }}>FREE</Text>
              <Text style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-primary)', textAlign: 'center', letterSpacing: '0.06em' }}>PRO</Text>
            </div>
            {PLAN_COMPARISON.map((row, i) => (
              <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '1fr 84px 84px', alignItems: 'center', padding: '9px 0', borderBottom: i < PLAN_COMPARISON.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <Text style={{ fontSize: 13, color: 'var(--text)' }}>{row.label}</Text>
                <div style={{ textAlign: 'center' }}>{cmpCell(row.free, false)}</div>
                <div style={{ textAlign: 'center' }}>{cmpCell(row.pro, true)}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 18, textAlign: 'center' }}>
            <Button
              type="primary" icon={<ThunderboltOutlined />}
              onClick={handleUpgrade} loading={upgrading}
              style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}
            >
              Upgrade to Pro — {PRICE_DISP}
            </Button>
          </div>
        </Card>
      )}

      {/* ── Pro upgrade banner (free plan only) ── */}
      {!isPro && (
        <Card
          style={{
            ...cardStyle,
            marginTop: 20,
            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
            border: 'none',
          }}
        >
          <Row align="middle" gutter={[24, 16]}>
            <Col xs={24} md={15}>
              <Space style={{ marginBottom: 12 }}>
                <CrownOutlined style={{ color: '#fbbf24', fontSize: 20 }} />
                <span style={{ fontWeight: 700, fontSize: 17, color: '#fff', letterSpacing: '-0.02em' }}>
                  Everything you need — {PRICE_DISP}
                </span>
              </Space>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
                {PRO_FEATURES.map(f => (
                  <Space key={f} size={6}>
                    <CheckCircleOutlined style={{ color: '#34d399', fontSize: 13 }} />
                    <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>{f}</Text>
                  </Space>
                ))}
              </div>
            </Col>
            <Col xs={24} md={9} style={{ textAlign: 'right' }}>
              <Button
                type="primary" size="large" icon={<ThunderboltOutlined />}
                onClick={handleUpgrade} loading={upgrading}
                style={{
                  background: '#fbbf24', borderColor: '#fbbf24',
                  color: '#1e1b4b', fontWeight: 700,
                  borderRadius: 'var(--r-lg)', height: 48, paddingInline: 28,
                }}
              >
                Upgrade to Pro
              </Button>
              <div style={{ marginTop: 8 }}>
                <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                  Cancel any time · No long-term commitment
                </Text>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* ── Billing details modal (shown before checkout if profile incomplete) ── */}
      <Modal
        title={<Space><BankOutlined />Billing Details</Space>}
        open={billingModal}
        onCancel={() => setBillingModal(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Text style={{ color: 'var(--text-3)', fontSize: 13, display: 'block', marginBottom: 20 }}>
          We need a few details for your invoices and receipts. You can edit these any time in
          Settings&nbsp;→&nbsp;Billing.
        </Text>
        <Form form={billingForm} layout="vertical" onFinish={handleBillingSubmit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="billing_name" label="Business / Legal Name"
                rules={[{ required: true, message: 'Business name is required' }]}>
                <Input placeholder="Acme Corp Ltd." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="billing_email" label="Billing Email"
                rules={[{ type: 'email', message: 'Enter a valid email' }]}>
                <Input placeholder="billing@yourcompany.com" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="billing_address1" label="Address Line 1">
            <Input placeholder={PH.addr1} />
          </Form.Item>
          <Form.Item name="billing_address2" label="Address Line 2 (optional)">
            <Input placeholder="Suite 100, Floor 3" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="billing_city" label="City">
                <Input placeholder={PH.city} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="billing_state" label="State / Province">
                <Input placeholder={PH.state} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="billing_postal_code" label="Postal Code">
                <Input placeholder={PH.postal} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="billing_country" label="Country"
                rules={[{ required: true, message: 'Country is required' }]}>
                <Input placeholder={PH.country} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="billing_tax_id" label="Tax ID / VAT (optional)">
                <Input placeholder={PH.tax} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setBillingModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={savingProfile || upgrading} icon={<ThunderboltOutlined />}>
              Continue to Payment
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Billing;
